/**
 * useExports — TanStack Query hooks for spec-010 export management.
 *
 * All requests are owner-authed (Firebase JWT via apiFetch).
 * This module exposes:
 *   - useListExports(vehicleId)  — polls GET /v1/vehicles/:id/exports
 *   - useCreateExport()          — mutation POST /v1/vehicles/:id/exports (202)
 *   - usePollExport(vehicleId, exportId) — polls GET …/:exportId until ready/failed
 *   - useRevokeExport()          — mutation POST …/:exportId/revoke
 *   - downloadExport(vehicleId, exportId) — authenticated blob download
 *
 * Key contract constraint: verifyUrl is returned ONLY in the 202 create
 * response and is NEVER available from the poll endpoint. Callers must
 * capture it from useCreateExport's onSuccess callback and hold it in
 * component state.
 *
 * Capacitor readiness:
 *   - No firebase/* imports — uses apiFetch (the auth abstraction layer).
 *   - No navigator.onLine — callers use useOnlineStatus().
 *   - No localStorage/sessionStorage for sensitive data.
 *   - No Timestamp / FieldValue — timestamps are ISO 8601 strings.
 *   - Download uses blob + URL.createObjectURL — no Web APIs without
 *     a Capacitor equivalent (Filesystem plugin handles blobs post-wrap).
 */

import { apiFetch } from '@/lib/api';
import { env } from '@/lib/env';
import { auth } from '@/lib/firebase';
import type {
  CreateExportRequest,
  CreateExportResponse,
  ExportSummary,
  ListExportsResponse,
  RevokeExportResponse,
} from '@/types/api';
import { useMutation, useQuery, type UseMutationResult } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const exportKeys = {
  list: (vehicleId: string) => ['exports', vehicleId] as const,
  detail: (vehicleId: string, exportId: string) => ['exports', vehicleId, exportId] as const,
  revoke: (vehicleId: string, exportId: string) =>
    ['exports', vehicleId, exportId, 'revoke'] as const,
};

// ---------------------------------------------------------------------------
// useListExports
// ---------------------------------------------------------------------------

export interface UseListExportsResult {
  data: ExportSummary[] | null;
  loading: boolean;
  error: Error | null;
}

/** Fetch the list of exports for a vehicle. Pass null to skip. */
export function useListExports(vehicleId: string | null): UseListExportsResult {
  const result = useQuery<ListExportsResponse, Error>({
    queryKey: exportKeys.list(vehicleId ?? ''),
    queryFn: () =>
      apiFetch<ListExportsResponse>(`/v1/vehicles/${vehicleId}/exports`, { method: 'GET' }),
    enabled: Boolean(vehicleId),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  return {
    data: result.data?.exports ?? null,
    loading: result.isLoading,
    error: result.error ?? null,
  };
}

// ---------------------------------------------------------------------------
// useCreateExport
// ---------------------------------------------------------------------------

export interface CreateExportInput {
  vehicleId: string;
  request: CreateExportRequest;
}

export type UseCreateExportResult = UseMutationResult<
  CreateExportResponse,
  Error,
  CreateExportInput
>;

/** Mutation for POST /v1/vehicles/:id/exports → 202. */
export function useCreateExport(): UseCreateExportResult {
  return useMutation<CreateExportResponse, Error, CreateExportInput>({
    mutationFn: ({ vehicleId, request }) =>
      apiFetch<CreateExportResponse>(`/v1/vehicles/${vehicleId}/exports`, {
        method: 'POST',
        body: request,
      }),
  });
}

// ---------------------------------------------------------------------------
// usePollExport
// ---------------------------------------------------------------------------

export interface UsePollExportResult {
  data: ExportSummary | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Poll GET /v1/vehicles/:id/exports/:exportId until status is 'ready' or 'failed'.
 * Pass null for either id to disable.
 *
 * Polling interval is 2s while pending/rendering; stops automatically once
 * the export reaches a terminal state (ready or failed).
 */
export function usePollExport(
  vehicleId: string | null,
  exportId: string | null,
): UsePollExportResult {
  const isTerminal = (status?: string) => status === 'ready' || status === 'failed';

  const result = useQuery<ExportSummary, Error>({
    queryKey: exportKeys.detail(vehicleId ?? '', exportId ?? ''),
    queryFn: () =>
      apiFetch<ExportSummary>(`/v1/vehicles/${vehicleId}/exports/${exportId}`, {
        method: 'GET',
      }),
    enabled: Boolean(vehicleId) && Boolean(exportId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return isTerminal(status) ? false : 2000;
    },
    staleTime: 0,
    gcTime: 5 * 60_000,
    // Don't retry on 4xx — the export is gone or not found.
    retry: (failureCount, error) => {
      if (error instanceof Error && 'status' in error) {
        const status = (error as { status?: number }).status;
        if (status !== undefined && status >= 400 && status < 500) return false;
      }
      return failureCount < 2;
    },
  });

  return {
    data: result.data ?? null,
    loading: result.isLoading,
    error: result.error ?? null,
  };
}

// ---------------------------------------------------------------------------
// useRevokeExport
// ---------------------------------------------------------------------------

export interface RevokeExportInput {
  vehicleId: string;
  exportId: string;
}

export type UseRevokeExportResult = UseMutationResult<
  RevokeExportResponse,
  Error,
  RevokeExportInput
>;

/**
 * Mutation for POST /v1/vehicles/:id/exports/:exportId/revoke → 200.
 *
 * Invalidates the verify token immediately — the public dossier and any
 * photo URLs derived from it go to not-available for all viewers.
 *
 * Graydon dependency: endpoint being added in parallel. If the endpoint
 * 404s in staging, the mutation will surface an error via the standard
 * actionableError path — the revoke button is present and errors are shown.
 */
export function useRevokeExport(): UseRevokeExportResult {
  return useMutation<RevokeExportResponse, Error, RevokeExportInput>({
    mutationFn: ({ vehicleId, exportId }) =>
      apiFetch<RevokeExportResponse>(`/v1/vehicles/${vehicleId}/exports/${exportId}/revoke`, {
        method: 'POST',
      }),
  });
}

// ---------------------------------------------------------------------------
// downloadExport — authenticated blob download
// ---------------------------------------------------------------------------

/**
 * Trigger an authenticated PDF download.
 *
 * Fetches the artifact bytes with a Bearer token (same as apiFetch) and
 * creates a temporary object URL to trigger the browser's download dialog.
 * The object URL is revoked immediately after click to avoid leaking memory.
 *
 * Post-Capacitor wrap: this function is the swap point — replace the
 * URL.createObjectURL path with @capacitor/filesystem write + share.
 */
export async function downloadExport(vehicleId: string, exportId: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');

  const token = await user.getIdToken();
  const response = await fetch(
    `${env.VITE_GG_API_BASE_URL}/v1/vehicles/${vehicleId}/exports/${exportId}/download`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  if (!response.ok) {
    if (response.status === 410) {
      throw new Error('This export has expired. Renew it and re-export to download again.');
    }
    if (response.status === 404) {
      throw new Error('Export not ready yet. Wait for it to finish rendering.');
    }
    throw new Error(`Download failed (${response.status}). Please try again.`);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  // Extract filename from Content-Disposition if present; fall back to a
  // sensible default. This avoids a raw URL appearing in the browser's
  // download shelf.
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const filenameMatch = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = filenameMatch?.[1] ?? `service-history-${exportId}.pdf`;

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
